import { apiUpload } from "@d12labs/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pager } from "@d12labs/ui/pager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Paperclip, Pencil, Sparkles, Trash2, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed
// Contact/Deal/Activity/Attachment.
import { crmApi } from "../api";
import {
	type Attachment,
	DEAL_STATUSES,
	type Deal,
	type DealStatus,
	describeActivity,
	formatBytes,
	formatMoney,
	formatRelativeTime,
} from "../types";

type ContactEditDraft = {
	name: string;
	email: string;
	company: string;
	notes: string;
};

export function ContactDetailPage() {
	const { id } = useParams({ strict: false }) as { id: string };
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState<ContactEditDraft>({
		name: "",
		email: "",
		company: "",
		notes: "",
	});

	const { data, isLoading, error } = useQuery({
		queryKey: ["crm", "contacts", id],
		queryFn: () => crmApi.contact({ params: { id } }),
	});

	const updateContact = useMutation({
		mutationFn: (payload: {
			name: string;
			email: string | null;
			company: string | null;
			notes: string | null;
		}) => crmApi.updateContact({ params: { id }, input: payload }),
		onSuccess: () => {
			setIsEditing(false);
			queryClient.invalidateQueries({ queryKey: ["crm", "contacts", id] });
			queryClient.invalidateQueries({ queryKey: ["crm", "contacts"] });
			toast.success("Contact updated");
		},
	});

	const deleteContact = useMutation({
		mutationFn: () => crmApi.removeContact({ params: { id } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["crm", "contacts"] });
			toast.success("Contact deleted");
			navigate({ to: "/crm" });
		},
	});

	if (isLoading) {
		return (
			<>
				<PageHeader eyebrow="CONTACTS" icon={User} title="Loading…" />
				<PageBody className="p-6">
					<div className="mx-auto w-full max-w-5xl">
						<p className="text-body-sm text-ink-subtle">Loading contact…</p>
					</div>
				</PageBody>
			</>
		);
	}

	if (error || !data) {
		return (
			<>
				<PageHeader eyebrow="CONTACTS" icon={User} title="Not found" />
				<PageBody className="p-6">
					<div className="mx-auto w-full max-w-5xl">
						<EmptyState
							icon={User}
							title="Contact not found"
							body={(error as Error)?.message ?? "This contact may have been deleted."}
							primary={
								<Link to="/crm">
									<Button variant="secondary">Back to contacts</Button>
								</Link>
							}
						/>
					</div>
				</PageBody>
			</>
		);
	}

	const { contact, deals } = data;

	const startEdit = () => {
		setDraft({
			name: contact.name,
			email: contact.email ?? "",
			company: contact.company ?? "",
			notes: contact.notes ?? "",
		});
		setIsEditing(true);
	};

	const saveEdit = () => {
		const name = draft.name.trim();
		if (name.length === 0) {
			toast.error("Name can't be empty");
			return;
		}
		updateContact.mutate({
			name,
			email: draft.email.trim() || null,
			company: draft.company.trim() || null,
			notes: draft.notes.trim() || null,
		});
	};

	const subtitle = [contact.company, contact.email].filter(Boolean).join(" · ") || undefined;

	return (
		<>
			<PageHeader
				eyebrow="CONTACTS"
				icon={User}
				title={contact.name}
				subtitle={subtitle}
				actions={
					isEditing ? (
						<>
							<Button variant="ghost" onClick={() => setIsEditing(false)}>
								Cancel
							</Button>
							<Button onClick={saveEdit} disabled={updateContact.isPending}>
								{updateContact.isPending ? "Saving…" : "Save"}
							</Button>
						</>
					) : (
						<>
							<Button variant="ghost" size="icon" onClick={startEdit} title="Edit contact">
								<Pencil className="size-4" />
							</Button>
							<ConfirmDialog
								title={`Delete ${contact.name}?`}
								description="This also removes all their deals and attachments. This action cannot be undone."
								confirmLabel="Delete"
								destructive
								onConfirm={() => deleteContact.mutate()}
							>
								<Button
									variant="ghost"
									size="icon"
									disabled={deleteContact.isPending}
									title="Delete contact"
								>
									<Trash2 className="size-4" />
								</Button>
							</ConfirmDialog>
						</>
					)
				}
			/>

			<PageBody className="p-0">
				<div className="grid h-full grid-cols-[1fr_300px] divide-x divide-hairline overflow-hidden">
					{/* Main column */}
					<div className="overflow-y-auto p-6 space-y-5">
						{/* Edit form */}
						{isEditing && (
							<Card>
								<CardHeader>
									<CardTitle>Edit contact</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
										<div className="flex flex-col gap-1.5">
											<Label htmlFor="edit-name">Name</Label>
											<Input
												id="edit-name"
												value={draft.name}
												onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
											/>
										</div>
										<div className="flex flex-col gap-1.5">
											<Label htmlFor="edit-email">Email</Label>
											<Input
												id="edit-email"
												type="email"
												value={draft.email}
												onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
											/>
										</div>
										<div className="flex flex-col gap-1.5 sm:col-span-2">
											<Label htmlFor="edit-company">Company</Label>
											<Input
												id="edit-company"
												value={draft.company}
												onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))}
											/>
										</div>
										<div className="flex flex-col gap-1.5 sm:col-span-2">
											<Label htmlFor="edit-notes">Notes</Label>
											<Textarea
												id="edit-notes"
												rows={4}
												value={draft.notes}
												onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
											/>
										</div>
									</div>
								</CardContent>
							</Card>
						)}

						{/* Notes card (read mode) */}
						{!isEditing && contact.notes && (
							<Card>
								<CardHeader>
									<CardTitle>Notes</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-body-sm text-ink-muted whitespace-pre-wrap">{contact.notes}</p>
								</CardContent>
							</Card>
						)}

						<SendEmailCard contactId={contact.id} disabled={!contact.email} />

						<DealsSection contactId={contact.id} deals={deals} />
					</div>

					{/* Right rail */}
					<aside className="overflow-y-auto bg-canvas p-5 space-y-6">
						{/* Details */}
						<div>
							<div className="text-eyebrow uppercase text-ink-tertiary mb-3">Details</div>
							<div className="space-y-2 text-body-sm">
								{(
									[
										[
											"Added",
											new Date(contact.createdAt).toLocaleDateString(undefined, {
												month: "short",
												day: "numeric",
												year: "numeric",
											}),
										],
										contact.company ? ["Company", contact.company] : null,
										contact.email ? ["Email", contact.email] : null,
									].filter(Boolean) as [string, string][]
								).map(([k, v]) => (
									<div key={k as string} className="flex gap-3">
										<span className="w-16 flex-none text-ink-subtle">{k}</span>
										<span className="min-w-0 flex-1 truncate text-ink-muted">{v}</span>
									</div>
								))}
							</div>
						</div>

						{/* Activity feed in rail */}
						<ActivityFeedRail contactId={contact.id} />

						{/* Deals summary */}
						{deals.length > 0 && (
							<div>
								<div className="text-eyebrow uppercase text-ink-tertiary mb-3">
									Deals ({deals.length})
								</div>
								<div className="space-y-2">
									{deals.map((d) => (
										<div key={d.id} className="flex items-center justify-between text-body-sm">
											<span className="truncate text-ink-muted">{d.title}</span>
											<Badge
												tone={
													d.status === "won" ? "success" : d.status === "lost" ? "danger" : "subtle"
												}
											>
												{d.status}
											</Badge>
										</div>
									))}
								</div>
							</div>
						)}
					</aside>
				</div>
			</PageBody>
		</>
	);
}

// --- Activity feed (right-rail compact version) ---

const ACTIVITY_TYPE_OPTIONS: { value: string; label: string }[] = [
	{ value: "", label: "All activity" },
	{ value: "email_sent", label: "Emails" },
	{ value: "deal_created", label: "Deals created" },
	{ value: "deal_status_changed", label: "Status changes" },
	{ value: "attachment_uploaded", label: "Uploads" },
];

const ACTIVITY_PAGE_SIZE = 10;

// Full activity feed (kept in main column below the fold, accessed via search params)
function ActivityFeed({ contactId }: { contactId: string }) {
	const search = useSearch({ strict: false }) as { activityType?: string; activityPage?: number };
	const navigate = useNavigate();
	const activityType = search.activityType ?? "";
	const activityPage = search.activityPage ?? 1;

	const { data, isLoading } = useQuery({
		queryKey: ["crm", "contacts", contactId, "activities", { activityType, activityPage }],
		// Typed against the def's query schema; an unknown `type` is ignored server-side.
		queryFn: () =>
			crmApi.activities({
				params: { id: contactId },
				query: {
					page: activityPage,
					pageSize: ACTIVITY_PAGE_SIZE,
					...(activityType ? { type: activityType } : {}),
				},
			}),
		refetchOnWindowFocus: true,
	});

	const total = data?.total ?? 0;
	const totalPages = data?.pageCount ?? 1;

	const setSearch = (next: { activityType?: string; activityPage?: number }) => {
		navigate({
			to: "/crm/contacts/$id",
			params: { id: contactId },
			search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
		});
	};

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle>Activity</CardTitle>
					<select
						value={activityType}
						onChange={(e) => setSearch({ activityType: e.target.value, activityPage: 1 })}
						className="text-meta border border-hairline rounded px-1.5 py-0.5 bg-surface-1 text-ink-muted"
					>
						{ACTIVITY_TYPE_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
				</div>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<p className="text-meta text-ink-subtle">Loading…</p>
				) : !data || data.items.length === 0 ? (
					<p className="text-body-sm text-ink-subtle">
						{activityType
							? "No matching activity for this filter."
							: "No activity yet. Send an email, add a deal, or upload a file to start the history."}
					</p>
				) : (
					<>
						<ul className="flex flex-col gap-2">
							{data.items.map((a) => (
								<li key={a.id} className="flex items-start justify-between gap-2">
									<span className="text-body-sm text-ink-muted truncate">
										{describeActivity(a)}
									</span>
									<span className="text-meta text-ink-subtle shrink-0">
										{formatRelativeTime(a.createdAt)}
									</span>
								</li>
							))}
						</ul>
						{total > ACTIVITY_PAGE_SIZE && (
							<Pager
								className="text-meta mt-3"
								page={activityPage}
								pageCount={totalPages}
								onPageChange={(p) => setSearch({ activityPage: p })}
								variant="ghost"
								label={
									<span className="text-ink-subtle">
										{total} total · page {activityPage} of {totalPages}
									</span>
								}
							/>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}

// Right-rail compact activity (first page only, no filter — used in the aside)
function ActivityFeedRail({ contactId }: { contactId: string }) {
	const { data } = useQuery({
		queryKey: ["crm", "contacts", contactId, "activities", { activityType: "", activityPage: 1 }],
		queryFn: () =>
			crmApi.activities({ params: { id: contactId }, query: { page: 1, pageSize: 5 } }),
		refetchOnWindowFocus: true,
	});

	const items = data?.items ?? [];

	return (
		<div>
			<div className="text-eyebrow uppercase text-ink-tertiary mb-3">Activity</div>
			{items.length === 0 ? (
				<p className="text-body-sm text-ink-subtle">No activity yet.</p>
			) : (
				<div className="space-y-3 text-body-sm">
					{items.map((a) => (
						<div key={a.id} className="flex items-start gap-2">
							<span className="mt-1 size-1.5 flex-none rounded-full bg-ink-tertiary" />
							<div className="min-w-0 flex-1">
								<div className="text-ink-muted truncate">{describeActivity(a)}</div>
								<div className="text-meta text-ink-subtle">{formatRelativeTime(a.createdAt)}</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

// --- Send email card ---

function SendEmailCard({ contactId, disabled }: { contactId: string; disabled: boolean }) {
	const queryClient = useQueryClient();
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");
	const [goal, setGoal] = useState("");
	const [sentAt, setSentAt] = useState<number | null>(null);

	const sendEmail = useMutation({
		mutationFn: (payload: { subject: string; body: string }) =>
			crmApi.sendEmail({ params: { id: contactId }, input: payload }),
		onSuccess: () => {
			setSubject("");
			setBody("");
			setGoal("");
			setSentAt(Date.now());
			// Refresh the activity feed — the chassis just logged an email_sent row.
			queryClient.invalidateQueries({ queryKey: ["crm", "contacts", contactId, "activities"] });
		},
	});

	const draftEmail = useMutation({
		mutationFn: (payload: { goal: string | null }) =>
			crmApi.draftEmail({ params: { id: contactId }, input: payload }),
		onSuccess: (emailDraft) => {
			setSubject(emailDraft.subject);
			setBody(emailDraft.body);
		},
	});

	const handleSend = (e: React.FormEvent) => {
		e.preventDefault();
		if (subject.trim().length === 0 || body.trim().length === 0) return;
		sendEmail.mutate({ subject: subject.trim(), body: body.trim() });
	};

	const handleDraft = () => {
		draftEmail.mutate({ goal: goal.trim() || null });
	};

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle>Send email</CardTitle>
					<div className="flex items-center gap-2">
						{draftEmail.data && (
							<span className="text-meta text-ink-subtle">
								AI draft loaded — edit before sending
							</span>
						)}
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleSend} className="flex flex-col gap-3">
					<div className="flex gap-2">
						<Input
							placeholder="What's this email about? (optional — for AI draft)"
							value={goal}
							onChange={(e) => setGoal(e.target.value)}
							disabled={draftEmail.isPending}
						/>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleDraft}
							disabled={draftEmail.isPending}
						>
							<Sparkles className="mr-1.5 size-3.5" />
							{draftEmail.isPending ? "Drafting…" : "Draft with AI"}
						</Button>
					</div>
					{draftEmail.error && (
						<p className="text-meta text-destructive">{(draftEmail.error as Error).message}</p>
					)}

					{disabled && (
						<p className="text-meta text-ink-subtle">No email address on file for this contact.</p>
					)}
					<Input
						placeholder="Subject"
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
						disabled={disabled}
					/>
					<Textarea
						placeholder="Message"
						value={body}
						onChange={(e) => setBody(e.target.value)}
						rows={6}
						disabled={disabled}
					/>
					{sendEmail.error && (
						<p className="text-meta text-destructive">{(sendEmail.error as Error).message}</p>
					)}
					{sentAt && (
						<p className="text-meta text-ink-subtle">
							Sent. (When RESEND_API_KEY is unset, the chassis logs to console instead of
							delivering.)
						</p>
					)}
					<div className="flex justify-end">
						<Button type="submit" disabled={disabled || sendEmail.isPending}>
							{sendEmail.isPending ? "Sending…" : "Send"}
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
}

// --- Deals section ---

function DealsSection({ contactId, deals }: { contactId: string; deals: Deal[] }) {
	const queryClient = useQueryClient();
	const [title, setTitle] = useState("");
	const [valueDollars, setValueDollars] = useState("");

	const invalidate = () => {
		// Prefix invalidation also picks up the contact's activities feed.
		queryClient.invalidateQueries({ queryKey: ["crm", "contacts", contactId] });
		queryClient.invalidateQueries({ queryKey: ["crm", "deals"] });
	};

	const createDeal = useMutation({
		mutationFn: (payload: { title: string; valueCents: number; contactId: string }) =>
			crmApi.createDeal({ input: payload }),
		onSuccess: () => {
			setTitle("");
			setValueDollars("");
			invalidate();
		},
	});

	const updateDeal = useMutation({
		mutationFn: ({ id, status }: { id: string; status: DealStatus }) =>
			crmApi.updateDeal({ params: { id }, input: { status } }),
		onSuccess: invalidate,
	});

	const deleteDeal = useMutation({
		mutationFn: (id: string) => crmApi.removeDeal({ params: { id } }),
		onSuccess: () => {
			invalidate();
			toast.success("Deal deleted");
		},
	});

	const handleAddDeal = (e: React.FormEvent) => {
		e.preventDefault();
		const dollars = Number.parseFloat(valueDollars || "0");
		if (title.trim().length === 0 || Number.isNaN(dollars) || dollars < 0) return;
		createDeal.mutate({
			title: title.trim(),
			valueCents: Math.round(dollars * 100),
			contactId,
		});
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>Deals</CardTitle>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleAddDeal} className="flex flex-col gap-2 mb-4">
					<div className="flex gap-2">
						<Input
							placeholder="Deal title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
						/>
						<Input
							type="number"
							min="0"
							step="0.01"
							placeholder="0.00"
							value={valueDollars}
							onChange={(e) => setValueDollars(e.target.value)}
							className="w-32"
						/>
						<Button type="submit" size="sm" disabled={createDeal.isPending}>
							Add
						</Button>
					</div>
					{createDeal.error && (
						<p className="text-meta text-destructive">{(createDeal.error as Error).message}</p>
					)}
				</form>

				{deals.length === 0 ? (
					<EmptyState
						small
						title="No deals yet"
						body="Add a deal above to start tracking pipeline value."
					/>
				) : (
					<ul className="flex flex-col gap-2">
						{deals.map((d) => (
							<li
								key={d.id}
								className="rounded-lg border border-hairline bg-surface-1 p-3 flex flex-col gap-2"
							>
								<div className="flex items-center justify-between gap-2">
									<div className="min-w-0">
										<div className="font-medium text-body-sm text-foreground truncate">
											{d.title}
										</div>
										<div className="text-meta text-ink-subtle">{formatMoney(d.valueCents)}</div>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<Label htmlFor={`status-${d.id}`} className="sr-only">
											Status
										</Label>
										<Select
											value={d.status}
											onValueChange={(v) =>
												updateDeal.mutate({ id: d.id, status: v as DealStatus })
											}
										>
											<SelectTrigger id={`status-${d.id}`} className="w-28 h-7 text-body-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{DEAL_STATUSES.map((s) => (
													<SelectItem key={s} value={s}>
														{s}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<ConfirmDialog
											title={`Delete "${d.title}"?`}
											description={`${formatMoney(d.valueCents)} · all attachments will be removed too.`}
											confirmLabel="Delete"
											destructive
											onConfirm={() => deleteDeal.mutate(d.id)}
										>
											<Button variant="ghost" size="icon" disabled={deleteDeal.isPending}>
												<Trash2 className="size-3.5" />
											</Button>
										</ConfirmDialog>
									</div>
								</div>
								<DealAttachments contactId={contactId} dealId={d.id} />
							</li>
						))}
					</ul>
				)}
			</CardContent>
		</Card>
	);
}

// --- Deal attachments ---

function DealAttachments({ contactId, dealId }: { contactId: string; dealId: string }) {
	const queryClient = useQueryClient();
	const { data: attachments } = useQuery({
		queryKey: ["crm", "deals", dealId, "attachments"],
		queryFn: () => crmApi.dealAttachments({ params: { id: dealId } }),
	});

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["crm", "deals", dealId, "attachments"] });
		// Upload logs an activity — refresh the contact's feed too.
		queryClient.invalidateQueries({ queryKey: ["crm", "contacts", contactId, "activities"] });
	};

	const upload = useMutation({
		mutationFn: (file: File) => {
			const fd = new FormData();
			fd.append("file", file);
			// Multipart POST through apiUpload — the typed client is JSON-only.
			return apiUpload<Attachment>(`/api/plugins/crm/deals/${dealId}/attachments`, fd);
		},
		onSuccess: invalidate,
	});

	const remove = useMutation({
		mutationFn: (id: string) => crmApi.removeAttachment({ params: { id } }),
		onSuccess: () => {
			invalidate();
			toast.success("Attachment deleted");
		},
	});

	const openAttachment = async (id: string) => {
		const { url } = await crmApi.attachmentUrl({ params: { id } });
		window.open(url, "_blank", "noopener,noreferrer");
	};

	return (
		<details className="text-body-sm">
			<summary className="cursor-pointer text-ink-subtle select-none flex items-center gap-1.5">
				<Paperclip className="size-3" />
				Files ({attachments?.length ?? 0})
			</summary>
			<div className="mt-2 flex flex-col gap-2 pl-4">
				<label className="flex items-center gap-2 text-meta">
					<input
						type="file"
						className="text-meta"
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								upload.mutate(file);
								e.target.value = "";
							}
						}}
						disabled={upload.isPending}
					/>
					{upload.isPending && <span className="text-ink-subtle">Uploading…</span>}
				</label>
				{upload.error && (
					<p className="text-meta text-destructive">{(upload.error as Error).message}</p>
				)}
				{attachments && attachments.length > 0 && (
					<ul className="flex flex-col gap-1.5">
						{attachments.map((a) => (
							<li key={a.id} className="flex items-center justify-between gap-2 text-body-sm">
								<button
									type="button"
									onClick={() => openAttachment(a.id)}
									className="text-primary-hover hover:underline truncate text-left"
								>
									{a.originalName}
								</button>
								<span className="text-ink-subtle shrink-0">{formatBytes(a.size)}</span>
								<ConfirmDialog
									title={`Delete "${a.originalName}"?`}
									description="Permanently removes the file from storage."
									confirmLabel="Delete"
									destructive
									onConfirm={() => remove.mutate(a.id)}
								>
									<button
										type="button"
										className="text-destructive shrink-0 text-meta"
										disabled={remove.isPending}
									>
										Delete
									</button>
								</ConfirmDialog>
							</li>
						))}
					</ul>
				)}
			</div>
		</details>
	);
}

// Keep the full ActivityFeed available for future tab implementation
export { ActivityFeed };
